import { Target, Star } from "lucide-react";
import { RecommendationStatus } from "@/lib/schema";

interface MatchScoreDisplayProps {
  matchScore: number | null;
  recommendation?: RecommendationStatus;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'target' | 'star';
  showLabel?: boolean;
  className?: string;
}

const getTargetIconColor = (recommendation: RecommendationStatus) => {
  switch (recommendation) {
    case RecommendationStatus.HIGHLY_RECOMMENDED: return 'text-green-600';
    case RecommendationStatus.RECOMMENDED: return 'text-blue-600';
    case RecommendationStatus.CONSIDER: return 'text-yellow-600';
    case RecommendationStatus.NOT_RECOMMENDED: return 'text-red-600';
    default: return 'text-gray-600';
  }
};

const getSizeClasses = (size: 'sm' | 'md' | 'lg') => {
  switch (size) {
    case 'sm':
      return {
        container: 'gap-1',
        icon: 'h-3 w-3',
        text: 'text-xs',
        label: 'text-xs'
      };
    case 'md':
      return {
        container: 'gap-1',
        icon: 'h-4 w-4',
        text: 'text-sm',
        label: 'text-sm'
      };
    case 'lg':
      return {
        container: 'gap-1',
        icon: 'h-5 w-5',
        text: 'text-2xl',
        label: 'text-sm'
      };
    default:
      return {
        container: 'gap-1',
        icon: 'h-4 w-4',
        text: 'text-sm',
        label: 'text-sm'
      };
  }
};

export function MatchScoreDisplay({
  matchScore,
  recommendation,
  size = 'md',
  variant = 'target',
  showLabel = false,
  className = ""
}: MatchScoreDisplayProps) {
  const sizeClasses = getSizeClasses(size);
  const score = matchScore || 0;
  
  // Choose icon based on variant
  const IconComponent = variant === 'star' ? Star : Target;
  
  // Get icon color based on recommendation or fallback to score-based color
  const getIconColor = () => {
    if (recommendation && variant === 'target') {
      return getTargetIconColor(recommendation);
    }
    
    // For star variant or when no recommendation, use score-based colors
    if (variant === 'star') {
      return 'text-orange-500'; // Consistent with existing usage
    }
    
    // Score-based colors for target when no recommendation
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (size === 'lg' && showLabel) {
    // Large format with label (like in AI insights section)
    return (
      <div className={`text-right ${className}`}>
        <div className={`flex items-center ${sizeClasses.container}`}>
          <IconComponent className={`${sizeClasses.icon} ${getIconColor()}`} />
          <span className={`${sizeClasses.text} font-bold text-gray-900 space-grotesk`}>
            {score}%
          </span>
        </div>
        <span className={`${sizeClasses.label} text-gray-500 space-grotesk`}>Match Score</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center ${sizeClasses.container} ${className}`}>
      <IconComponent className={`${sizeClasses.icon} ${getIconColor()}`} />
      <span className={`${sizeClasses.text} font-medium text-gray-900 space-grotesk`}>
        {score}%{showLabel && size !== 'lg' ? ' Match' : ''}
      </span>
    </div>
  );
}
